import React from 'react'
import Head from 'next/head'
import { Text, View, CompanyLogo, Icon, Loading } from '@/components'
import { Settings } from '@/config'
import { LayoutPanel, useIsTablet } from '@/hooks'
import { createStyles } from '@codeleap/styles'
import { NavigationMenu } from './Header/NavigationMenu'
import { PropsOf } from '@codeleap/types'

export type LayoutProps = {
  children?: React.ReactNode
  pageTitle?: string
  leftContent?: React.ReactNode
  variant?: 'accessHub' | 'default'
  painelStyle?: PropsOf<typeof View>['style']
  painelContentStyle?: PropsOf<typeof View>['style']
  isGroupChat?: boolean
}

const BREAKPOINT = 'tabletSmall'

export const Layout = ({ children, pageTitle = 'Connect', leftContent, variant, painelStyle, painelContentStyle, isGroupChat = false }: LayoutProps) => {
  const appendedTitle = ` | ${Settings.AppName}`
  const headTitle = pageTitle ? `${pageTitle} ${appendedTitle}` : Settings.AppName

  const isTabletSmall = useIsTablet()
  const { showLeftPanel, showRightPanel } = LayoutPanel.useLayoutPanel()
  const shouldRenderRightPanel = !isTabletSmall || (isTabletSmall && children)

  const isAccessHub = variant === 'accessHub'
  const hasLeftContent = !!leftContent

  const shouldShowRightPanel = isAccessHub && !hasLeftContent ? true : showRightPanel

  React.useEffect(() => {
    if (!isTabletSmall) return

    const preventGestureBack = (e: TouchEvent) => {
      const touch = e.touches[0]
      const edgeThreshold = 50 // pixels from edge

      if (touch.clientX < edgeThreshold || touch.clientX > window.innerWidth - edgeThreshold) {
        const startX = touch.clientX
        const startY = touch.clientY

        const handleTouchMove = (moveEvent: TouchEvent) => {
          const moveTouch = moveEvent.touches[0]
          const deltaX = Math.abs(moveTouch.clientX - startX)
          const deltaY = Math.abs(moveTouch.clientY - startY)

          if (deltaX > deltaY && deltaX > 10) {
            moveEvent.preventDefault()
          }
        }

        const cleanup = () => {
          document.removeEventListener('touchmove', handleTouchMove)
          document.removeEventListener('touchend', cleanup)
        }

        document.addEventListener('touchmove', handleTouchMove, { passive: false })
        document.addEventListener('touchend', cleanup)
      }
    }

    document.addEventListener('touchstart', preventGestureBack, { passive: true })

    return () => {
      document.removeEventListener('touchstart', preventGestureBack)
    }
  }, [isTabletSmall])

  return <>
    <Head><title>{headTitle}</title></Head>

    <View style={styles.layout}>
      {!isTabletSmall && (
        <NavigationMenu
          showLogo
          style={['compact', styles.navMenu]}
        />
      )}

      {leftContent && (
        <View style={[
          styles.leftContent,
          isGroupChat && styles.leftContentGroup,
          isTabletSmall && (showLeftPanel ? styles.slideIn : styles.slideOutLeft),
        ]}>
          {leftContent}
        </View>
      )}

      {shouldRenderRightPanel && (
        <View style={[
          styles.panelContent,
          isTabletSmall && (shouldShowRightPanel ? styles.slideIn : styles.slideOutRight),
          painelStyle,
        ]}>
          <View style={[styles.content, painelContentStyle]}>
            {children}
          </View>
        </View>
      )}
    </View>
  </>
}

export type LayoutPlaceholderProps = {
  error?: string
  loading?: boolean
}

Layout.Placeholder = ({ error, loading }: LayoutPlaceholderProps) => {

  if (!!error) {
    return (
      <View style={['center', 'column', 'gap:1']}>
        <View style={['row', 'gap:0.5', 'alignCenter']}>
          <Icon name='info-circle' style={['error', 'size:2']} />
          <Text text={error} style={['p1', 'medium', 'text:center', 'error']} />
        </View>
      </View>)
  }

  if (loading) {
    return (
      <View style={['center', 'column', 'gap:1']}>
        <Loading />
      </View>
    )
  }

  return (
    <View style={['center', 'column', 'gap:1']}>
      <View style={['row', 'gap:0.5', 'alignCenter']}>
        <CompanyLogo />
        <Text text={'Connect'} style={['h5', 'bold', 'color:primarySolid500']} />
      </View>

      <Text text='Live on Web version' style={['p3', 'color:neutralSolid600']} />
    </View>
  )
}

const styles = createStyles(theme => ({
  navMenu: {
    marginRight: theme.spacing.value(3),

    [theme.media.down('tablet')]: {
      marginRight: theme.spacing.value(1),
    },
  },

  layout: {
    position: 'relative',

    width: '100vw',
    height: '100vh',
    ...theme.presets.row,
    ...theme.presets.alignStart,
    ...theme.presets.justifyStart,
    ...theme.spacing.py(3),
    ...theme.spacing.pr(3),
    backgroundColor: '#F4F4F4',
    paddingLeft: theme.spacing.value(3),

    [theme.media.down(BREAKPOINT)]: {
      ...theme.spacing.padding(0),
      overscrollBehaviorX: 'none',
      touchAction: 'pan-y pinch-zoom',
    },
  },

  leftContent: {
    display: 'flex',
    ...theme.presets.alignStart,
    ...theme.presets.justifyStart,
    borderTopLeftRadius: theme.radius.regular,
    borderBottomLeftRadius: theme.radius.regular,
    overflow: 'hidden',
    width: '100vw',
    maxWidth: 400,
    height: '100%',
    backgroundColor: theme.baseColors.neutralSolid0,
    minWidth: 300,

    [theme.media.down(BREAKPOINT)]: {
      borderRadius: 0,

      width: '100vw',
      maxWidth: 'none',

      position: 'absolute',
      transition: 'transform 0.5s ease',
      overscrollBehaviorX: 'none',
      touchAction: 'pan-y pinch-zoom',
    },
  },
  leftContentGroup: {
    minWidth: 360,
    flexShrink: 1,

    [theme.media.down('tablet')]: {
      minWidth: 350,
    },

    [theme.media.down(BREAKPOINT)]: {
      minWidth: 'unset',
      flexShrink: 'unset',
    },
  },
  panelContent: {
    ...theme.presets.fullWidth,
    ...theme.presets.fullHeight,
    minWidth: 0,
    backgroundColor: theme.colors.bgDefault,
    borderRadius: theme.radius.regular,

    ...theme.spacing.padding(0),

    [theme.media.down(BREAKPOINT)]: {
      borderRadius: 0,
      width: '100vw',
      position: 'absolute',
      transition: 'transform 0.5s ease',
      ...theme.spacing.padding(0),
      overscrollBehaviorX: 'none',
      touchAction: 'pan-y pinch-zoom',

      [theme.media.down(BREAKPOINT)]: {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      },
    },

    [theme.media.down('tablet')]: {

    },
  },
  content: {
    ...theme.presets.fullWidth,
    ...theme.presets.fullHeight,
    ...theme.presets.center,
    ...theme.presets.column,
    ...theme.spacing.gap(1),
    overflow: 'hidden',
    borderRadius: theme.radius.regular,
    backgroundColor: theme.baseColors.neutralSolid0,

    [theme.media.down(BREAKPOINT)]: {
      borderRadius: 0,
    },
  },

  slideIn: {
    transform: 'translateX(0%)',
  },
  slideOutLeft: {
    transform: 'translateX(-100%)',
  },
  slideOutRight: {
    transform: 'translateX(100%)',
  },
}))
